import { createContext, useState, useMemo, ReactNode } from 'react'

export interface PageHeaderConfig {
  breadcrumb?: ReactNode
  title?: ReactNode
  extra?: ReactNode
}

// Separate contexts to prevent unnecessary re-renders
// Components that only SET the header won't re-render when header VALUE changes
interface PageHeaderStateContextValue {
  pageHeader: PageHeaderConfig
}

interface PageHeaderActionsContextValue {
  setPageHeader: (config: PageHeaderConfig) => void
  clearPageHeader: () => void
}

export const PageHeaderStateContext = createContext<PageHeaderStateContextValue | undefined>(undefined)
export const PageHeaderActionsContext = createContext<PageHeaderActionsContextValue | undefined>(undefined)

interface PageHeaderProviderProps {
  children: ReactNode
}

export const PageHeaderProvider = ({ children }: PageHeaderProviderProps) => {
  const [pageHeader, setPageHeaderState] = useState<PageHeaderConfig>({})

  // Actions are stable and never change - memoize once
  const actions = useMemo(() => ({
    setPageHeader: (config: PageHeaderConfig) => {
      setPageHeaderState(config)
    },
    clearPageHeader: () => {
      setPageHeaderState({})
    },
  }), []) // Empty deps - actions are stable

  // State context value changes when pageHeader changes
  const stateValue = useMemo(
    () => ({ pageHeader }),
    [pageHeader]
  )

  return (
    <PageHeaderActionsContext.Provider value={actions}>
      <PageHeaderStateContext.Provider value={stateValue}>
        {children}
      </PageHeaderStateContext.Provider>
    </PageHeaderActionsContext.Provider>
  )
}
